import Images from 'app/assets/img';
import { ReactComponent as CopyIcon } from 'app/assets/img/copy-icon.svg';
import _ from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { selectTranslations } from '../App/slice/selectors';
import Countdown from './Countdown';
import useWindowSize from 'app/hooks/useWindowSize';

interface IBookReservationProps {
  dataInfo: any;
  onClickPaymentNow?: any;
  onTimeout?: any;
  onClickDetail?: any;
}
const BookReservation: React.FC<IBookReservationProps> = props => {
  const { dataInfo, onClickPaymentNow, onTimeout, onClickDetail } = props;
  const { device } = useWindowSize();
  const isMobile = device === 'mobile';
  const translations = useSelector(selectTranslations);

  const [copied, setCopied] = useState(false);
  const timerRef = useRef<any>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(dataInfo.orderCode || '');
      setCopied(true);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {}
  };
  const isSaleMng = useMemo(() => {
    const subOrderState = _.isEmpty(dataInfo?.subOrderState) ? true : false;
    return subOrderState;
  }, [dataInfo?.subOrderState]);
  return (
    <Card>
      <StatusIcon>✓</StatusIcon>
      <Title>{translations?.['b2b.txt.holdBookingSuccess']}</Title>
      <SubTitle>{translations?.['b2b.txt.bookingCode']}</SubTitle>
      <CodeWrapper $isMobile={isMobile}>
        <Code>{dataInfo.orderCode}</Code>
        <CopyButton onClick={handleCopy}>
          {copied ? <TickIcon>✓</TickIcon> : <CopyIconCss />}
        </CopyButton>
      </CodeWrapper>
      <Note>
        {
          translations?.[
            isSaleMng
              ? 'b2b.txt.pleasePayBeforeDeadline'
              : 'b2b.txt.waitForApproval'
          ]
        }
      </Note>
      {!_.isEmpty(dataInfo) && (
        <ExpireBox>
          <img
            src={Images.iconPaymentInprogress}
            width={100}
            height={100}
            alt=""
          />
          <div>
            <ExpireText>{translations?.['b2b.txt.paymentDeadline']}</ExpireText>
            <ExpireTime>
              <Countdown onTimeout={onTimeout} orderCheckout={dataInfo} />
            </ExpireTime>
          </div>
        </ExpireBox>
      )}
      <Actions>
        <SecondaryBtn onClick={onClickDetail}>
          {translations?.['b2b.txt.viewBookingCodeDetails']}
        </SecondaryBtn>
        <PrimaryBtn disabled={!isSaleMng} onClick={onClickPaymentNow}>
          {translations?.['b2b.txt.pay-now']}
        </PrimaryBtn>
      </Actions>
    </Card>
  );
};
export default BookReservation;
const Card = styled.div`
  background: #fff;
  width: 100%;
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  position: relative;
  margin-bottom: 20px;
`;
const StatusIcon = styled.div`
  width: 32px;
  height: 32px;
  background: #00954d;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  font-weight: 700;
`;
const Title = styled.h2`
  margin: 0 0 12px;
  font-size: 24px;
  font-weight: 600;
`;
const SubTitle = styled.p`
  margin: 0 0 8px;
  color: #000;
  font-size: 14px;
`;
const CodeWrapper = styled.div<{ $isMobile?: boolean }>`
  display: inline-flex;
  align-items: center;
  background: var(--color-9);
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
  height: ${({ $isMobile }) => ($isMobile ? '54px' : '44px')};
`;
const Code = styled.div`
  color: #fff;
  font-weight: 600;
  padding: 10px 16px;
  font-size: 18px;
  letter-spacing: 1px;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
`;
const CopyButton = styled.div`
  background: #fff1e3;
  border: none;
  padding: 0 12px;
  cursor: pointer;
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;
const Note = styled.p`
  margin: 8px 0 16px;
  font-size: 14px;
`;
const ExpireBox = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff1e3;
  padding: 8px 12px;
  border-radius: 8px;
  @media (max-width: 768px) {
    position: static;
    justify-content: center;
    margin-bottom: 16px;
  }
`;
const ExpireText = styled.div`
  font-size: 14px;
  color: #555;
  margin-bottom: 6px;
`;

const ExpireTime = styled.div`
  font-weight: 600;
  font-size: 20px;
  color: var(--color-9);
  text-align: right;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const PrimaryBtn = styled.button`
  background: var(--color-gradient);
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
  &:hover {
    opacity: 0.8;
  }
  :disabled {
    background: var(--color-8);
  }
`;

const SecondaryBtn = styled.button`
  background: #f2f2f2;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    opacity: 0.8;
  }
`;
const CopyIconCss = styled(CopyIcon)`
  height: 20px;
  filter: brightness(0) saturate(100%) invert(64%) sepia(63%) saturate(1246%)
    hue-rotate(345deg) brightness(99%) contrast(101%);
`;
const TickIcon = styled.div`
  height: 30px;
  width: 30px;
  color: var(--color-9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
`;
